// @flow
import { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { auth } from '../services/firebase';
import SPBackend from '../services/SPBackend';
import { log } from '../utils/jsUtils';

/**
 * Custom hook that provides SPBackend methods pre-configured with Firebase auth user.
 *
 * This hook automatically handles the confusion between:
 * - Redux user state (for UI data: uid, email, accountId, etc.)
 * - Firebase auth user (for API authentication with .getIdToken() method)
 *
 * @returns {Object} SPBackend instance methods that automatically use auth.currentUser
 *
 * @example
 * const backend = useAuthenticatedBackend();
 *
 * // NO LONGER NEED TO PASS USER:
 * backend.getPaymentMethods()  // ✅ Correct
 *   .then(response => {
 *     // Handle response
 *   });
 *
 * // BEFORE (BROKEN):
 * const user = useSelector(selectUser);
 * SPBackend.getPaymentMethods(user);  // ❌ Wrong user type!
 */
export const useAuthenticatedBackend = () => {
  const dispatch = useDispatch();

  return useMemo(() => {
    const firebaseUser = auth.currentUser;

    if (!firebaseUser) {
      log('[useAuthenticatedBackend] ERROR: No Firebase user available');

      // Show error toast
      dispatch({
        type: 'ACTION_SET_SNACK_MSG',
        snackMessage: {
          message: 'Authentication session expired. Please log in again.',
          level: 'error',
          duration: 5000
        }
      });

      // Log out user and redirect to login
      setTimeout(() => {
        auth.signOut()
          .then(() => {
            log('[useAuthenticatedBackend] User logged out due to missing Firebase auth');
            window.location.href = '/login';
          })
          .catch(err => {
            log('[useAuthenticatedBackend] Error logging out:', err);
            // Force redirect anyway
            window.location.href = '/login';
          });
      }, 1000); // Give time for toast to show

      // Return null backend that fails gracefully
      return createNullBackend();
    }

    log('[useAuthenticatedBackend] Firebase user authenticated:', firebaseUser.uid);

    // Return wrapper object with all SPBackend methods pre-bound with firebaseUser
    return {
      // ==================== Accounts/Billing ====================
      getAccount: () => SPBackend.getAccount(firebaseUser),

      switchAccount: (accountId) =>
        SPBackend.switchAccount(firebaseUser, accountId),

      setPushSubscriberId: (pushPlayerId) =>
        SPBackend.setPushSubscriberId(firebaseUser, pushPlayerId),

      setAccountOnboardingSettings: (isRequired) =>
        SPBackend.setAccountOnboardingSettings(isRequired, firebaseUser),

      getPlanInfo: (planId) =>
        SPBackend.getPlanInfo(planId, firebaseUser),

      getCouponInfo: (couponId) =>
        SPBackend.getCouponInfo(couponId, firebaseUser),

      getAccountInvoices: () =>
        SPBackend.getAccountInvoices(firebaseUser),

      getPaymentMethods: () =>
        SPBackend.getPaymentMethods(firebaseUser),

      updatePaymentMethod: (paymentMethod) =>
        SPBackend.updatePaymentMethod(paymentMethod, firebaseUser),

      chargePlan: (paymentPayload) =>
        SPBackend.chargePlan(paymentPayload, firebaseUser),

      subscribeWithoutPayment: () =>
        SPBackend.subscribeWithoutPayment(firebaseUser),

      getMembersTopupPlans: () =>
        SPBackend.getMembersTopupPlans(firebaseUser),

      purchaseTopupPlan: (planId, paymentMethod) =>
        SPBackend.purchaseTopupPlan(firebaseUser, planId, paymentMethod),

      getReferralLink: () =>
        SPBackend.getReferralLink(firebaseUser),

      getCustomIdToken: (uid) =>
        SPBackend.getCustomIdToken(uid, firebaseUser),

      // ==================== Phone/Twilio ====================
      fetchPhoneNumbers: (searchPayload) =>
        SPBackend.fetchPhoneNumbers(firebaseUser, searchPayload),

      buyPhoneNumber: (buyNumberPayload) =>
        SPBackend.buyPhoneNumber(firebaseUser, buyNumberPayload),

      sendCodeToVerifyNb: (phoneToVerify) =>
        SPBackend.sendCodeToVerifyNb(firebaseUser, phoneToVerify),

      verifyPhoneNbCode: (phoneToVerify, code) =>
        SPBackend.verifyPhoneNbCode(firebaseUser, phoneToVerify, code),

      enableInboundCalls: (accountId) =>
        SPBackend.enableInboundCalls(accountId, firebaseUser),

      disableInboundCalls: (accountId) =>
        SPBackend.disableInboundCalls(accountId, firebaseUser),

      // ==================== Tasks/Feed ====================
      getFilteredFeed: (options, filterSearchText) =>
        SPBackend.getFilteredFeed(options, filterSearchText, firebaseUser),

      getRequestsForCSV: (options, filterSearchText) =>
        SPBackend.getRequestsForCSV(options, filterSearchText, firebaseUser),

      uploadConversationImage: (file) =>
        SPBackend.uploadConversationImage(file, firebaseUser),

      getRequest: (requestId) =>
        SPBackend.getRequest(requestId, firebaseUser),

      createRequest: (newRequest) =>
        SPBackend.createRequest(newRequest, firebaseUser),

      renameTask: (taskId, newTaskName) =>
        SPBackend.renameTask(taskId, newTaskName, firebaseUser),

      updateRequest: (requestId, newStatus) =>
        SPBackend.updateRequest(requestId, newStatus, firebaseUser),

      sendRequestNote: (requestId, message) =>
        SPBackend.sendRequestNote(requestId, message, firebaseUser),

      getRequestHistory: (taskId) =>
        SPBackend.getRequestHistory(taskId, firebaseUser),

      getTaskUnreadCount: (taskId) =>
        SPBackend.getTaskUnreadCount(taskId, firebaseUser),

      getFilterOptions: () =>
        SPBackend.getFilterOptions(firebaseUser),

      // ==================== Tags ====================
      getTags: (param = 'simple') =>
        SPBackend.getTags(param, firebaseUser),

      setTag: (tag) =>
        SPBackend.setTag(tag, firebaseUser),

      editTag: (tag) =>
        SPBackend.editTag(tag, firebaseUser),

      tagCustomers: (tagId, customerIds) =>
        SPBackend.tagCustomers(tagId, customerIds, firebaseUser),

      removeTagFromCustomers: (tagId, customerIds) =>
        SPBackend.removeTagFromCustomers(tagId, customerIds, firebaseUser),

      archiveTag: (tagId) =>
        SPBackend.archiveTag(tagId, firebaseUser),

      // ==================== Buildings/Properties ====================
      getBuildings: () =>
        SPBackend.getBuildings(firebaseUser),

      getBuilding: (buildingKey) =>
        SPBackend.getBuilding(buildingKey, firebaseUser),

      createBuilding: (newBuilding) =>
        SPBackend.createBuilding(newBuilding, firebaseUser),

      removeBuilding: (propertyKey) =>
        SPBackend.removeBuilding(propertyKey, firebaseUser),

      editBuilding: (propertyKey, changedPropertyName) =>
        SPBackend.editBuilding(propertyKey, changedPropertyName, firebaseUser),

      findBuildings: (searchVal) =>
        SPBackend.findBuildings(searchVal, firebaseUser),

      sendEmailRequestForNewNumber: (payload) =>
        SPBackend.sendEmailRequestForNewNumber(firebaseUser, payload),

      // ==================== Units ====================
      findUnits: (searchVal) =>
        SPBackend.findUnits(searchVal, firebaseUser),

      findCommonUnits: (searchVal) =>
        SPBackend.findCommonUnits(searchVal, firebaseUser),

      createUnit: (newUnit) =>
        SPBackend.createUnit(newUnit, firebaseUser),

      removeUnit: (roomKey) =>
        SPBackend.removeUnit(roomKey, firebaseUser),

      editUnit: (unit, roomKey) =>
        SPBackend.editUnit(unit, roomKey, firebaseUser),

      // ==================== Tenants ====================
      createTenant: (tenant) =>
        SPBackend.createTenant(tenant, firebaseUser),

      findTenants: (findTenantsPayload) =>
        SPBackend.findTenants(findTenantsPayload, firebaseUser),

      findTenantsIds: (tagIds) =>
        SPBackend.findTenantsIds(tagIds, firebaseUser),

      editTenant: (payload) =>
        SPBackend.editTenant(payload, firebaseUser),

      moveTenantOut: (payload) =>
        SPBackend.moveTenantOut(payload, firebaseUser),

      removeTenant: (id) =>
        SPBackend.removeTenant(id, firebaseUser),

      sendPMToTenant: (tenantId, taskId = '', messagePayload) =>
        SPBackend.sendPMToTenant(tenantId, taskId, messagePayload, firebaseUser),

      sendPMToTenants: (tenantIds, messagePayload) =>
        SPBackend.sendPMToTenants(tenantIds, messagePayload, firebaseUser),

      uploadTenantFile: (file) =>
        SPBackend.uploadTenantFile(file, firebaseUser),

      // ==================== Customer Import ====================
      getImportPresignedUrl: (fileName) =>
        SPBackend.getImportPresignedUrl(fileName, firebaseUser),

      startTenantImport: (jobId) =>
        SPBackend.startTenantImport(jobId, firebaseUser),

      processTenantImport: (jobId, columnMapping) =>
        SPBackend.processTenantImport(jobId, columnMapping, firebaseUser),

      getTenantImportStatus: (jobId) =>
        SPBackend.getTenantImportStatus(jobId, firebaseUser),

      // ==================== Staff/Users ====================
      getUsers: () =>
        SPBackend.getUsers(firebaseUser),

      getCurrentUser: () =>
        SPBackend.getCurrentUser(firebaseUser),

      findStaffMembers: (searchVal) =>
        SPBackend.findStaffMembers(searchVal, firebaseUser),

      assignStaffToTasks: (taskIds, assigneeId) =>
        SPBackend.assignStaffToTasks(taskIds, assigneeId, firebaseUser),

      addUserPermission: (payload) =>
        SPBackend.addUserPermission(payload, firebaseUser),

      removeUserPermission: (payload) =>
        SPBackend.removeUserPermission(payload, firebaseUser),

      createStaff: (payload) =>
        SPBackend.createStaff(payload, firebaseUser),

      removeStaff: (staff) =>
        SPBackend.removeStaff(staff, firebaseUser),

      editStaff: (staff, changedStaff) =>
        SPBackend.editStaff(staff, changedStaff, firebaseUser),

      // ==================== Keywords/Bot ====================
      findKeywordTriggers: () =>
        SPBackend.findKeywordTriggers(firebaseUser),

      editKeywordTrigger: (keywordPayload, isNewKeyword) =>
        SPBackend.editKeywordTrigger(keywordPayload, firebaseUser, isNewKeyword),

      removeKeywordTrigger: (id) =>
        SPBackend.removeKeywordTrigger(id, firebaseUser),

      getAccountBotSettings: () =>
        SPBackend.getAccountBotSettings(firebaseUser),

      setAccountBotSettings: (payload) =>
        SPBackend.setAccountBotSettings(firebaseUser, payload),

      // ==================== Analytics ====================
      getMainAnalyticsKPIs: (days) =>
        SPBackend.getMainAnalyticsKPIs(firebaseUser, days),

      getGraph: (days) =>
        SPBackend.getGraph(firebaseUser, days),

      getAnalyticsProperties: (days) =>
        SPBackend.getAnalyticsProperties(firebaseUser, days),

      // ==================== Other ====================
      setAgreementSignature: (payload) =>
        SPBackend.setAgreementSignature(payload, firebaseUser),
    };
  }, [dispatch]); // Empty deps for firebaseUser - accessed fresh each time inside methods
};

/**
 * Creates a null backend object that fails gracefully when no Firebase user is authenticated.
 * All methods return rejected promises with a descriptive error.
 *
 * @returns {Object} Proxy object that returns promise rejections for any method call
 */
const createNullBackend = () => {
  const nullMethod = () =>
    Promise.reject(new Error('No authenticated Firebase user available. Please log in.'));

  // Use Proxy to handle any method call
  return new Proxy({}, {
    get: () => nullMethod
  });
};

export default useAuthenticatedBackend;
